import { useRef, useState } from "react";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Herbruikbare admin-upload component.
 * Klikt → kiest bestand → uploadt naar /api/admin/upload-image → roept onChange(url) aan.
 *
 * Props:
 *  - value: string (huidige image_url)
 *  - onChange: (url: string) => void
 *  - testId: string (optioneel, voor data-testid prefix)
 *  - aspect: "square" | "wide" (default square)
 */
export default function ImageUpload({ value, onChange, testId = "image-upload", aspect = "square" }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    const handleFiles = async (files) => {
        if (!files || files.length === 0) return;
        const file = files[0];
        if (!file.type.startsWith("image/")) {
            toast.error("Kies een afbeelding (JPG, PNG, WEBP)");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Bestand te groot (max 8 MB)");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload-image", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            // bouw absolute URL ivm CDN-cache buster niet nodig; backend zit op zelfde origin
            onChange(data.url);
            toast.success("Foto geüpload");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload mislukt");
        } finally {
            setUploading(false);
        }
    };

    const aspectClass = aspect === "wide" ? "aspect-[16/9]" : "aspect-square";
    const hasImage = !!value;

    return (
        <div className="space-y-2" data-testid={testId}>
            <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    handleFiles(e.dataTransfer.files);
                }}
                onClick={() => !uploading && inputRef.current?.click()}
                className={`relative ${aspectClass} border-2 border-dashed cursor-pointer transition-colors overflow-hidden ${
                    dragOver ? "border-rose-600 bg-rose-50" : hasImage ? "border-zinc-200" : "border-zinc-300 hover:border-zinc-500 bg-zinc-50"
                }`}
            >
                {hasImage ? (
                    <>
                        <img
                            src={value}
                            alt="preview"
                            className="absolute inset-0 w-full h-full object-cover"
                            onError={(e) => { e.target.style.opacity = "0.2"; }}
                        />
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onChange(""); }}
                            data-testid={`${testId}-clear`}
                            className="absolute top-2 right-2 w-7 h-7 bg-white border border-zinc-200 hover:bg-rose-50 hover:border-rose-300 flex items-center justify-center text-zinc-700 hover:text-rose-700"
                            title="Verwijder foto">
                            <X size={14} />
                        </button>
                        {!uploading && (
                            <div className="absolute bottom-2 left-2 right-2 bg-zinc-950/80 backdrop-blur text-white text-[10px] font-bold uppercase tracking-widest py-1.5 text-center opacity-0 hover:opacity-100 transition-opacity">
                                Klik om te vervangen
                            </div>
                        )}
                    </>
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-500 gap-2 p-4 text-center">
                        {uploading ? (
                            <>
                                <Loader2 size={28} className="animate-spin" />
                                <p className="text-xs">Uploaden...</p>
                            </>
                        ) : (
                            <>
                                <Upload size={28} strokeWidth={1.5} />
                                <p className="text-xs font-bold uppercase tracking-widest">Klik of sleep een foto</p>
                                <p className="text-[10px] text-zinc-400">JPG · PNG · WEBP · max 8 MB</p>
                            </>
                        )}
                    </div>
                )}

                {uploading && hasImage && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                        <Loader2 size={24} className="animate-spin text-zinc-700" />
                    </div>
                )}
            </div>

            <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid={`${testId}-input`}
            />

            {/* Manuele URL fallback */}
            <details className="text-[10px]">
                <summary className="cursor-pointer text-zinc-400 uppercase tracking-widest">Of plak een URL</summary>
                <input
                    type="text"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="https://..."
                    className="mt-1.5 w-full border border-zinc-200 px-2 py-1.5 text-xs font-mono"
                    data-testid={`${testId}-url`}
                />
            </details>
        </div>
    );
}
